import CloseIcon from '@mui/icons-material/Close'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import RichTextEditorMSWordStyle from 'src/common/richTextEditor/RichTextEditorMSWordStyle'
import {
  prepareSupportAgentEmail,
  sendSupportAgentEmail,
} from 'src/supportPortal/services/supportAgentContactService'

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// The server's prepared draft body is plain text; turn it into simple paragraphs/line-breaks so the
// rich editor opens with a sensible starting point the agent can format further.
function plainTextToHtml(text) {
  if (!text) {
    return ''
  }
  return String(text)
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br/>')}</p>`)
    .join('')
}

// True when the composed HTML carries real content (text or an image), not just empty markup.
function hasContent(html) {
  if (!html) {
    return false
  }
  const text = html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim()
  return text.length > 0 || /<img/i.test(html)
}

export default function SupportEmailComposerDialog({
  open,
  onClose,
  context,
  targetName,
  targetEmail: targetEmailProp,
  onSent,
}) {
  const [loadingDraft, setLoadingDraft] = useState(false)
  const [prepareError, setPrepareError] = useState('')
  const [targetEmail, setTargetEmail] = useState(targetEmailProp || '')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    let active = true
    if (!open) {
      setLoadingDraft(false)
      setPrepareError('')
      setSubject('')
      setBody('')
      setSending(false)
      return () => {
        active = false
      }
    }

    async function loadDraft() {
      setLoadingDraft(true)
      setPrepareError('')
      const result = await prepareSupportAgentEmail(context)
      if (!active) {
        return
      }
      if (result?.ok) {
        setTargetEmail(result.data?.email || targetEmailProp || '')
        setSubject(result.data?.subject || '')
        setBody(plainTextToHtml(result.data?.body || ''))
      } else {
        // Still allow composing from scratch, but surface why the draft couldn't be prefilled.
        setTargetEmail(targetEmailProp || '')
        setPrepareError(result?.message || 'Could not load a draft — you can still compose the email.')
      }
      setLoadingDraft(false)
    }

    loadDraft()
    return () => {
      active = false
    }
  }, [open, context, targetEmailProp])

  async function handleSend() {
    if (!subject.trim()) {
      toast.error('Please add a subject')
      return
    }
    if (!hasContent(body)) {
      toast.error('Please write a message')
      return
    }
    setSending(true)
    const result = await sendSupportAgentEmail(context, subject.trim(), body)
    setSending(false)
    if (result?.ok) {
      toast.success(`Email sent${targetEmail ? ` to ${targetEmail}` : ''}`)
      onSent?.(result)
      onClose?.()
      return
    }
    toast.error(result?.message || 'Could not send the email.')
  }

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailRoundedIcon sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Compose email
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {targetName ? `To ${targetName}` : 'Send a message'} — delivered from AUMHUM Support.
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={sending} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {loadingDraft ? (
          <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Preparing draft…
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {prepareError ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {prepareError}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }} useFlexGap>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                RECIPIENT
              </Typography>
              {targetEmail ? (
                <Chip size="small" label={targetEmail} sx={{ fontWeight: 600 }} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Resolved on send
                </Typography>
              )}
            </Stack>

            <TextField
              label="Subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              size="small"
              fullWidth
            />

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                MESSAGE
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <RichTextEditorMSWordStyle
                  value={body}
                  onChange={setBody}
                  placeholder="Write your message…"
                  minHeight="220px"
                />
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending || loadingDraft}
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            backgroundColor: '#4f46e5',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#4338ca', boxShadow: 'none' },
          }}
        >
          {sending ? 'Sending…' : 'Send email'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
